import React, { useEffect, useMemo, useState } from "react";
import { Button, Separator, Surface } from "@heroui/react";
import { ClipboardCheck, Code2, UserRound } from "lucide-react";
import { LoadingState } from "../components/LoadingState";
import { PortalOnboardingStepper } from "../components/PortalOnboardingStepper";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { showErrorToast, showSuccessToast } from "../lib/toast";

function replaceEmbedToken(value, token) {
  if (!value) return "";
  return value.replace(/YOUR_EMBED_TOKEN/g, token || "YOUR_EMBED_TOKEN");
}

function SnippetBlock({ title, hint, value, copyLabel, copiedLabel }) {
  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(value);
      showSuccessToast(copiedLabel);
    } catch {
      showErrorToast(copyLabel);
    }
  };

  return (
    <div className="portal-panel__code-wrap">
      <h3 className="portal-panel__section-title">{title}</h3>
      {hint ? <p className="portal-panel__description">{hint}</p> : null}
      <pre className="portal-panel__code">{value}</pre>
      <Button variant="secondary" onPress={copySnippet}>
        {copyLabel}
      </Button>
    </div>
  );
}

function InstructionList({ title, items }) {
  return (
    <div className="portal-onboarding-instructions">
      {title ? <h3 className="portal-panel__section-title">{title}</h3> : null}
      <ol className="portal-panel__steps">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ol>
    </div>
  );
}

function StatusRow({ label, active }) {
  return (
    <span className={`portal-panel__status ${active ? "portal-panel__status--positive" : ""}`}>
      <span className="portal-panel__status-dot" aria-hidden="true" />
      {label}
    </span>
  );
}

export function OnboardingPage() {
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [snippet, setSnippet] = useState(null);
  const [embedToken, setEmbedToken] = useState("");
  const [currentStep, setCurrentStep] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [setup, snip] = await Promise.all([
        apiFetch("/api/portal/setup_status"),
        apiFetch("/api/portal/embed_snippet")
      ]);
      setStatus(setup);
      setSnippet(snip);
      if (setup.embed_token) setEmbedToken(setup.embed_token);
    } catch {
      showErrorToast(t("onboarding.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const verified = status?.verification_status || {};
  const shop = status?.shop || {};
  const scriptHtml = replaceEmbedToken(snippet?.script_html, embedToken);
  const iframeHtml = replaceEmbedToken(snippet?.iframe_html, embedToken);
  const guaranteesHtml = replaceEmbedToken(snippet?.guarantees_script_html, embedToken);
  const aiLabelsHtml = replaceEmbedToken(snippet?.ai_labels_script_html, embedToken);

  const stepCompletion = useMemo(
    () => ({
      account: true,
      embed: false,
      test: Boolean(verified.tested)
    }),
    [verified.tested]
  );

  const steps = useMemo(
    () => [
      {
        id: "account",
        title: t("onboarding.stepAccountTitle"),
        description: t("onboarding.stepAccountDescription"),
        icon: <UserRound size={14} strokeWidth={2} />,
        complete: stepCompletion.account
      },
      {
        id: "embed",
        title: t("onboarding.stepEmbedTitle"),
        description: t("onboarding.stepEmbedDescription"),
        icon: <Code2 size={14} strokeWidth={2} />,
        complete: stepCompletion.embed
      },
      {
        id: "test",
        title: t("onboarding.stepTestTitle"),
        description: t("onboarding.stepTestDescription"),
        icon: <ClipboardCheck size={14} strokeWidth={2} />,
        complete: stepCompletion.test
      }
    ],
    [stepCompletion, t]
  );

  useEffect(() => {
    if (loading) return;

    if (stepCompletion.test) {
      setCurrentStep(2);
      return;
    }

    if (verified.embed_configured) {
      setCurrentStep(1);
      return;
    }

    setCurrentStep(0);
  }, [loading, stepCompletion.test, verified.embed_configured]);

  const markTested = async () => {
    try {
      await apiFetch("/api/portal/setup_status/mark_tested", { method: "POST" });
      showSuccessToast(t("onboarding.testMarked"));
      await load();
      setCurrentStep(2);
    } catch {
      showErrorToast(t("onboarding.testUpdateError"));
    }
  };

  const regenerateEmbedToken = async () => {
    try {
      const data = await apiFetch("/api/portal/embed_token/regenerate", { method: "POST" });
      setEmbedToken(data.embed_token || "");
      showSuccessToast(t("onboarding.embedTokenRegenerated"));
    } catch {
      showErrorToast(t("onboarding.embedTokenRegenerateError"));
    }
  };

  if (loading) return <LoadingState label={t("common.loadingOnboarding")} />;

  const accountInstructions = [
    t("onboarding.stepAccountInstruction1"),
    t("onboarding.stepAccountInstruction2"),
    t("onboarding.stepAccountInstruction3")
  ];

  const embedInstructions = [
    t("onboarding.integrationStep1"),
    t("onboarding.integrationStep2"),
    t("onboarding.integrationStep3"),
    t("onboarding.integrationStep4")
  ];

  const testInstructions = [
    t("onboarding.stepTestInstruction1"),
    t("onboarding.stepTestInstruction2"),
    t("onboarding.stepTestInstruction3")
  ];

  return (
    <div>
      <div className="portal-onboarding-layout">
        <PortalOnboardingStepper
          steps={steps}
          currentStep={currentStep}
          onStepChange={setCurrentStep}
          ariaLabel={t("onboarding.timelineTitle")}
        />

        <Surface className="portal-onboarding-step-card" variant="secondary">
          {currentStep === 0 ? (
            <>
              <h3 className="portal-panel__section-title">{t("onboarding.stepAccountTitle")}</h3>
              <p className="portal-panel__description">{t("onboarding.stepAccountIntro")}</p>
              <InstructionList title={t("onboarding.stepAccountChecklistTitle")} items={accountInstructions} />
              <div className="portal-panel__status-row">
                <StatusRow
                  label={`${t("onboarding.embedConfigured")}: ${verified.embed_configured ? t("common.yes") : t("common.no")}`}
                  active={verified.embed_configured}
                />
                {shop.default_locale ? (
                  <StatusRow
                    label={t("onboarding.stepAccountLocale", { locale: shop.default_locale })}
                    active
                  />
                ) : null}
              </div>
              <div className="portal-panel__actions">
                <Button variant="primary" className="portal-accent-button" onPress={() => setCurrentStep(1)}>
                  {t("onboarding.continueToEmbed")}
                </Button>
              </div>
            </>
          ) : null}

          {currentStep === 1 ? (
            <>
              <h3 className="portal-panel__section-title">{t("onboarding.stepEmbedTitle")}</h3>
              <p className="portal-panel__description">{t("onboarding.stepEmbedIntro")}</p>
              <InstructionList title={t("onboarding.integrationTitle")} items={embedInstructions} />

              {embedToken ? (
                <div className="portal-panel__code-wrap">
                  <h3 className="portal-panel__section-title">{t("onboarding.embedTokenPrompt")}</h3>
                  <pre className="portal-panel__code">{embedToken}</pre>
                </div>
              ) : (
                <div className="portal-panel__code-wrap">
                  <p className="portal-panel__description">{t("onboarding.embedTokenMissing")}</p>
                  <Button variant="secondary" onPress={regenerateEmbedToken}>
                    {t("onboarding.regenerateEmbedToken")}
                  </Button>
                </div>
              )}

              {scriptHtml ? (
                <SnippetBlock
                  title={t("onboarding.embedScript")}
                  value={scriptHtml}
                  copyLabel={t("onboarding.copySnippet")}
                  copiedLabel={t("onboarding.snippetCopied")}
                />
              ) : null}

              {scriptHtml && iframeHtml ? (
                <div className="portal-onboarding-method-divider" role="presentation">
                  <Separator className="portal-onboarding-method-divider__line" />
                  <span className="portal-onboarding-method-divider__label">
                    {t("onboarding.stepEmbedMethodDivider")}
                  </span>
                  <Separator className="portal-onboarding-method-divider__line" />
                </div>
              ) : null}

              {iframeHtml ? (
                <SnippetBlock
                  title={t("onboarding.iframeEmbed")}
                  hint={t("onboarding.iframeEmbedHint")}
                  value={iframeHtml}
                  copyLabel={t("onboarding.copySnippet")}
                  copiedLabel={t("onboarding.snippetCopied")}
                />
              ) : null}

              {guaranteesHtml ? (
                <SnippetBlock
                  title={t("onboarding.guaranteesEmbed") || "Guarantees / GARAN embed"}
                  hint={t("onboarding.guaranteesEmbedHint") || "Paste on product pages. Set data-product-key to your product SKU."}
                  value={guaranteesHtml}
                  copyLabel={t("onboarding.copySnippet")}
                  copiedLabel={t("onboarding.snippetCopied")}
                />
              ) : null}

              {aiLabelsHtml ? (
                <SnippetBlock
                  title={t("onboarding.aiLabelsEmbed") || "AI Labels embed"}
                  hint={t("onboarding.aiLabelsEmbedHint") || "Paste on product pages with AI-marked media."}
                  value={aiLabelsHtml}
                  copyLabel={t("onboarding.copySnippet")}
                  copiedLabel={t("onboarding.snippetCopied")}
                />
              ) : null}

              <div className="portal-panel__actions">
                <Button variant="secondary" onPress={() => setCurrentStep(0)}>
                  {t("onboarding.back")}
                </Button>
                <Button variant="primary" className="portal-accent-button" onPress={() => setCurrentStep(2)}>
                  {t("onboarding.continueToTest")}
                </Button>
              </div>
            </>
          ) : null}

          {currentStep === 2 ? (
            <>
              <h3 className="portal-panel__section-title">{t("onboarding.stepTestTitle")}</h3>
              <p className="portal-panel__description">{t("onboarding.stepTestIntro")}</p>
              <InstructionList title={t("onboarding.stepTestChecklistTitle")} items={testInstructions} />
              <div className="portal-panel__status-row">
                <StatusRow
                  label={`${t("onboarding.tested")}: ${verified.tested ? t("common.yes") : t("common.no")}`}
                  active={verified.tested}
                />
              </div>
              <div className="portal-panel__actions">
                <Button variant="secondary" onPress={() => setCurrentStep(1)}>
                  {t("onboarding.back")}
                </Button>
                <Button variant="primary" className="portal-accent-button" onPress={markTested}>
                  {t("onboarding.markTested")}
                </Button>
                <Button variant="secondary" onPress={load}>
                  {t("common.refresh")}
                </Button>
              </div>
            </>
          ) : null}
        </Surface>
      </div>
    </div>
  );
}
